import { newBlock } from './contractBlocks';

// Import a Word document into the block model. Chunk 1.7, spec §19.
//
// mammoth turns .docx into clean, predictable HTML; this turns that HTML into
// sections. The conversion is deliberately LOSSY AND HONEST about it: the
// block model has six named styles and three inline marks, so a Word file's
// fonts, colors, tables, images, columns and text boxes have nowhere to go.
// Rather than approximate them, anything that cannot be represented is dropped
// and REPORTED, so the person importing sees exactly what changed before they
// accept it. A silently mangled clause in an agreement is far worse than a
// visible "2 tables were not imported".
//
// The HTML is parsed by a tokenizer written here rather than DOMParser, so the
// whole conversion runs in node and is pinned by scripts/test-docx-import.mjs.
// mammoth's own output is the only HTML this ever sees.

const BLOCK_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'li', 'table', 'hr', 'div']);
const VOID_TAGS = new Set(['br', 'img', 'hr', 'col', 'input']);

const ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  ldquo: '“', rdquo: '”', lsquo: '‘', rsquo: '’',
  mdash: '—', ndash: '–', hellip: '…', bull: '•',
};

function decodeEntities(s) {
  return String(s).replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (m, body) => {
    if (body[0] === '#') {
      const cp = body[1] === 'x' || body[1] === 'X'
        ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(cp) && cp > 0 ? String.fromCodePoint(cp) : m;
    }
    return Object.prototype.hasOwnProperty.call(ENTITIES, body) ? ENTITIES[body] : m;
  });
}

/** HTML -> a tree of { tag, children } and { text } nodes. Mammoth's subset only. */
export function parseHtml(html) {
  const root = { tag: 'root', children: [] };
  const stack = [root];
  const re = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/?)>|([^<]+)/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    const [raw, tag, selfClose, text] = m;
    if (text != null) {
      const decoded = decodeEntities(text);
      if (decoded) stack[stack.length - 1].children.push({ text: decoded });
      continue;
    }
    const name = tag.toLowerCase();
    if (raw[1] === '/') {
      // Close the nearest matching open tag; ignore a stray close rather than
      // unwinding the whole document.
      for (let i = stack.length - 1; i > 0; i--) {
        if (stack[i].tag === name) { stack.length = i; break; }
      }
      continue;
    }
    const node = { tag: name, children: [] };
    stack[stack.length - 1].children.push(node);
    if (!selfClose && !VOID_TAGS.has(name)) stack.push(node);
  }
  return root;
}

/**
 * Inline content -> flat runs carrying their marks. Collected first and
 * serialized second, because emphasis has to wrap a contiguous span: walking
 * the tree and emitting markers per text node turns <em>Very
 * <strong>important</strong></em> into overlapping markers that mean nothing.
 */
function inlineRuns(node, marks, report, out = []) {
  for (const child of node.children || []) {
    if (child.text != null) {
      const t = child.text.replace(/\s+/g, ' ');
      if (!t) continue;
      // Our inline vocabulary is * and **, so an asterisk typed in Word would
      // read as emphasis. We never rewrite the words of an agreement, so it is
      // counted and reported instead.
      if (t.includes('*')) report.literal_asterisks += 1;
      out.push({ text: t, ...marks });
      continue;
    }
    const tag = child.tag;
    if (tag === 'br') { out.push({ text: ' ', ...marks }); continue; }
    if (tag === 'img') { report.dropped.images += 1; continue; }
    // A list nested in a list item is handled by the list walker, not here.
    if (tag === 'ul' || tag === 'ol') continue;
    inlineRuns(child, {
      bold: marks.bold || tag === 'strong' || tag === 'b',
      italic: marks.italic || tag === 'em' || tag === 'i',
    }, report, out);
  }
  return out;
}

/** Runs -> **bold** / *italic*, one marker pair per contiguous span. */
function serializeRuns(runs) {
  let out = '';
  let i = 0;
  while (i < runs.length) {
    const { bold, italic } = runs[i];
    let text = '';
    while (i < runs.length && runs[i].bold === bold && runs[i].italic === italic) {
      text += runs[i].text;
      i += 1;
    }
    if (!text) continue;
    // Marking leading or trailing whitespace reads as a stray marker, so the
    // emphasis wraps the words and the spaces stay outside it.
    const lead = text.match(/^\s*/)[0];
    const tail = text.match(/\s*$/)[0];
    const core = text.slice(lead.length, text.length - tail.length);
    const mark = bold && italic ? '***' : bold ? '**' : italic ? '*' : '';
    out += core ? lead + mark + core + mark + tail : text;
  }
  return out;
}

function inlineText(node, _marks, report) {
  return serializeRuns(inlineRuns(node, { bold: false, italic: false }, report));
}

const HEADING = { h1: 'heading1', h2: 'heading2', h3: 'heading3', h4: 'heading3', h5: 'heading3', h6: 'heading3' };

function listItems(node, report) {
  const items = [];
  for (const child of node.children || []) {
    if (child.tag === 'li') {
      const text = inlineText(child, undefined, report).trim();
      if (text) items.push(text);
      // A list nested inside a list item is flattened: the model has one level.
      for (const sub of child.children || []) {
        if (sub.tag === 'ul' || sub.tag === 'ol') {
          report.flattened_lists += 1;
          items.push(...listItems(sub, report));
        }
      }
    }
  }
  return items;
}

/** The empty report shape, so callers can rely on every key existing. */
function emptyReport() {
  return {
    blocks: 0,
    headings: 0,
    paragraphs: 0,
    lists: 0,
    flattened_lists: 0,
    literal_asterisks: 0,
    dropped: { tables: 0, images: 0, empty_paragraphs: 0 },
    notes: [],
    messages: [],
  };
}

export function htmlToBlocks(html) {
  const report = emptyReport();
  const root = parseHtml(html);
  const blocks = [];

  const walk = (node) => {
    for (const child of node.children || []) {
      if (child.text != null) {
        // Loose text between blocks: keep it rather than lose a sentence.
        const t = child.text.trim();
        if (t) { blocks.push(newBlock('paragraph', { text: t })); report.paragraphs += 1; }
        continue;
      }
      const tag = child.tag;
      if (HEADING[tag]) {
        const text = inlineText(child, undefined, report).trim();
        if (text) { blocks.push(newBlock(HEADING[tag], { text })); report.headings += 1; }
        continue;
      }
      if (tag === 'p') {
        const text = inlineText(child, undefined, report).trim();
        if (text) { blocks.push(newBlock('paragraph', { text })); report.paragraphs += 1; }
        else report.dropped.empty_paragraphs += 1;
        continue;
      }
      if (tag === 'ul' || tag === 'ol') {
        const items = listItems(child, report);
        if (items.length) {
          blocks.push(newBlock(tag === 'ul' ? 'bullet_list' : 'numbered_list', { items }));
          report.lists += 1;
        }
        continue;
      }
      if (tag === 'hr') { blocks.push(newBlock('divider')); continue; }
      if (tag === 'table') { report.dropped.tables += 1; continue; }
      if (tag === 'img') { report.dropped.images += 1; continue; }
      // div, body, article and anything else: descend.
      if (BLOCK_TAGS.has(tag) || tag === 'body' || tag === 'html') { walk(child); continue; }
      const text = inlineText(child, undefined, report).trim();
      if (text) { blocks.push(newBlock('paragraph', { text })); report.paragraphs += 1; }
    }
  };
  walk(root);

  report.blocks = blocks.length;

  if (report.dropped.tables) {
    report.notes.push(`${report.dropped.tables} table${report.dropped.tables === 1 ? '' : 's'} could not be imported — TimeKeep documents have no tables. Any wording inside them is not in the sections below, so add it by hand.`);
  }
  if (report.dropped.images) {
    report.notes.push(`${report.dropped.images} image${report.dropped.images === 1 ? '' : 's'} were left out. A logo or letterhead belongs in Page setup instead.`);
  }
  if (report.flattened_lists) {
    report.notes.push(`${report.flattened_lists} nested list${report.flattened_lists === 1 ? ' was' : 's were'} flattened to a single level.`);
  }
  if (report.literal_asterisks) {
    report.notes.push(`${report.literal_asterisks} piece${report.literal_asterisks === 1 ? '' : 's'} of text contain a * character. TimeKeep reads * as italic, so check those sections in the preview — the words themselves were not changed.`);
  }
  if (!blocks.length) {
    report.notes.push('Nothing could be read out of that file. If it was saved as .doc rather than .docx, re-save it as .docx and try again.');
  }

  return { blocks, report };
}

/**
 * The whole import: .docx -> sections + a report of what changed.
 * mammoth is loaded on demand so its ~500KB never lands in the main bundle.
 */
export async function convertDocx(file) {
  const mammoth = await import('mammoth/mammoth.browser');
  const arrayBuffer = await file.arrayBuffer();
  const result = await (mammoth.default || mammoth).convertToHtml({ arrayBuffer });
  const { blocks, report } = htmlToBlocks(result.value || '');
  report.messages = (result.messages || [])
    .filter((m) => m.type === 'warning' || m.type === 'error')
    .map((m) => m.message)
    // Mammoth reports every unmapped Word style, often the same one repeatedly.
    .filter((m, i, a) => a.indexOf(m) === i)
    .slice(0, 12);
  return { blocks, report, html: result.value || '' };
}

/** SHA-256 of the original file, so the import is provable years later. */
export async function fileSha256(file) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
